// The pricing calculator's arithmetic: what a proposed deal would bill, what
// reaches us after the partner's share, what it would cost to run, and what
// that leaves, month by month over the contract.
//
// Nothing here reads the API. The page hands it a cost basis (measured from
// last month's customers, or typed), a plan (how the deal is priced), the
// shares, and a volume, and every figure is derived from those. Amounts are
// minor units of the scenario's one currency; a unit price is a fractional
// count of minor units, because a minute costs a fraction of a cent.
//
// The margin is the Margin page's: our share of the RECURRING revenue less the
// cost we carry, over our share of the recurring revenue. Implementation fees
// are one-time and stay out of it; the contract timeline adds them back.

import type { MarginCustomer, MarginTotals } from "@crm/shared";
import { assertNever } from "../../lib/assertNever.js";

/** How the usage is priced. */
export type UsagePricing =
  /** A price per minute, billed to the second. */
  | "per_minute"
  /** A price per minute, each call rounded up to its own whole minute. */
  | "per_minute_whole"
  /** A flat price per call (conversation), however long it ran. */
  | "per_call";

export const USAGE_PRICINGS: readonly UsagePricing[] = [
  "per_minute",
  "per_minute_whole",
  "per_call",
];

/** The unit a prepaid block is stated in. */
export type AllowanceUnit = "minutes" | "calls";

/** What a call costs us to run. */
export interface CostBasis {
  /** Everything the AI's part of a call costs, per minute of it. */
  costPerMinuteMinor: number;
  /** How long the AI holds a call, in minutes. */
  minutesPerCall: number;
  /**
   * What rounding each call up to its whole minute adds to it, in minutes. Half
   * a minute when calls end anywhere in their last minute; measured from the
   * comparables when there are any.
   */
  roundUpMinutesPerCall: number;
}

export interface ImplementationFee {
  amountMinor: number;
  /** The contract month it is billed in, 1-based. */
  month: number;
}

/** How the deal is priced. */
export interface Plan {
  usage: UsagePricing;
  /** Per minute or per call, by `usage`. */
  rateMinor: number;
  /** A flat monthly hosting fee: the platform, whatever it is used for. */
  hostingMinor: number;
  prepaid: PrepaidBlock;
  implementation: readonly ImplementationFee[];
}

/**
 * Usage bought up front each month: `quantity` minutes or calls for
 * `amountMinor`, billed whether or not they are used. Usage past the block is
 * billed at the plan's rate. A block of nothing is no block.
 */
export interface PrepaidBlock {
  quantity: number;
  unit: AllowanceUnit;
  amountMinor: number;
}

export const NO_PREPAID: PrepaidBlock = { quantity: 0, unit: "minutes", amountMinor: 0 };

/** Our share of each kind of money, 0-100. A direct deal is 100 across. */
export interface Shares {
  recurringPct: number;
  implementationPct: number;
  /** Our share of the cost. 100 means we carry all of it, as we do metered usage. */
  costPct: number;
}

export interface Volume {
  callsPerMonth: number;
  /** Months to reach full volume. Zero starts at full volume. */
  rampMonths: number;
  /** The first month's share of full volume, 0-100. */
  rampStartPct: number;
}

export interface Scenario {
  cost: CostBasis;
  plan: Plan;
  shares: Shares;
  volume: Volume;
  termMonths: number;
  /** What the customer pays a person to handle a call. */
  humanCostPerCallMinor: number;
}

/** One month of a scenario at a given volume. */
export interface MonthFigures {
  calls: number;
  /** The AI's minutes, which is what the cost follows. */
  minutes: number;
  /** Units the usage price applies to: minutes (rounded or not) or calls. */
  billedUnits: number;
  /** Billed units beyond the prepaid block. */
  overageUnits: number;
  hostingMinor: number;
  prepaidMinor: number;
  /** What the units past the prepaid block bill at the rate. */
  usageGrossMinor: number;
  recurringGrossMinor: number;
  recurringOursMinor: number;
  costMinor: number;
  ourCostMinor: number;
  contributionMinor: number;
  /** Null with no recurring revenue of ours to take a margin of. */
  marginPct: number | null;
}

/** Minutes billed per call under a pricing: rounded up per call, or not. */
export function billedMinutesPerCall(usage: UsagePricing, cost: CostBasis): number {
  return usage === "per_minute_whole"
    ? cost.minutesPerCall + cost.roundUpMinutesPerCall
    : cost.minutesPerCall;
}

/** The unit a pricing's rate is per. */
export function usageUnit(usage: UsagePricing): AllowanceUnit {
  switch (usage) {
    case "per_minute":
    case "per_minute_whole":
      return "minutes";
    case "per_call":
      return "calls";
    default:
      return assertNever(usage, "usage pricing");
  }
}

/** The prepaid block, stated in the units the rate bills. */
function prepaidInBilledUnits(plan: Plan, cost: CostBasis): number {
  const { quantity, unit } = plan.prepaid;
  if (unit === usageUnit(plan.usage)) return quantity;
  const perCall = billedMinutesPerCall(plan.usage, cost);
  // Minutes prepaid, priced per call: the calls those minutes hold.
  if (unit === "minutes") return perCall === 0 ? 0 : quantity / perCall;
  // Calls prepaid, priced per minute: the minutes those calls bill.
  return quantity * perCall;
}

/** What one prepaid unit comes to. Null with no block. */
export function prepaidUnitPriceMinor(prepaid: PrepaidBlock): number | null {
  return prepaid.quantity <= 0 ? null : prepaid.amountMinor / prepaid.quantity;
}

export function monthAt(scenario: Scenario, calls: number): MonthFigures {
  const { cost, plan, shares } = scenario;
  const minutes = calls * cost.minutesPerCall;
  const billedUnits =
    plan.usage === "per_call" ? calls : calls * billedMinutesPerCall(plan.usage, cost);
  const overageUnits = Math.max(0, billedUnits - prepaidInBilledUnits(plan, cost));
  const usageGross = overageUnits * plan.rateMinor;
  const recurringGross = plan.hostingMinor + plan.prepaid.amountMinor + usageGross;
  const recurringOurs = (recurringGross * shares.recurringPct) / 100;
  const costMinor = minutes * cost.costPerMinuteMinor;
  const ourCost = (costMinor * shares.costPct) / 100;
  const contribution = recurringOurs - ourCost;
  return {
    calls,
    minutes,
    billedUnits,
    overageUnits,
    hostingMinor: plan.hostingMinor,
    prepaidMinor: plan.prepaid.amountMinor,
    usageGrossMinor: usageGross,
    recurringGrossMinor: recurringGross,
    recurringOursMinor: recurringOurs,
    costMinor,
    ourCostMinor: ourCost,
    contributionMinor: contribution,
    marginPct: recurringOurs <= 0 ? null : (contribution * 100) / recurringOurs,
  };
}

/** The scenario once it has reached full volume. */
export function steadyState(scenario: Scenario): MonthFigures {
  return monthAt(scenario, scenario.volume.callsPerMonth);
}

/** The share of full volume in a contract month (1-based): a straight line up to full. */
export function rampFactor(month: number, volume: Volume): number {
  if (volume.rampMonths <= 0 || month > volume.rampMonths) return 1;
  const start = clamp(volume.rampStartPct, 0, 100) / 100;
  return start + ((1 - start) * (month - 1)) / volume.rampMonths;
}

export interface TimelineMonth extends MonthFigures {
  month: number;
  oneTimeGrossMinor: number;
  oneTimeOursMinor: number;
  /** Recurring contribution plus our share of the month's one-time fees. */
  totalContributionMinor: number;
  cumulativeContributionMinor: number;
  /** What the customer would pay people to handle the same calls. */
  humanCostMinor: number;
  /** Everything the customer is billed this month: recurring and one-time, gross. */
  customerBillMinor: number;
  savingsMinor: number;
  cumulativeSavingsMinor: number;
}

export function timeline(scenario: Scenario): TimelineMonth[] {
  const months: TimelineMonth[] = [];
  let cumulative = 0;
  let cumulativeSavings = 0;
  const term = Math.max(1, Math.round(scenario.termMonths));
  for (let month = 1; month <= term; month += 1) {
    const figures = monthAt(
      scenario,
      scenario.volume.callsPerMonth * rampFactor(month, scenario.volume),
    );
    const oneTimeGross = scenario.plan.implementation
      .filter((fee) => fee.month === month)
      .reduce((sum, fee) => sum + fee.amountMinor, 0);
    const oneTimeOurs = (oneTimeGross * scenario.shares.implementationPct) / 100;
    const total = figures.contributionMinor + oneTimeOurs;
    cumulative += total;
    const human = figures.calls * scenario.humanCostPerCallMinor;
    const bill = figures.recurringGrossMinor + oneTimeGross;
    cumulativeSavings += human - bill;
    months.push({
      ...figures,
      month,
      oneTimeGrossMinor: oneTimeGross,
      oneTimeOursMinor: oneTimeOurs,
      totalContributionMinor: total,
      cumulativeContributionMinor: cumulative,
      humanCostMinor: human,
      customerBillMinor: bill,
      savingsMinor: human - bill,
      cumulativeSavingsMinor: cumulativeSavings,
    });
  }
  return months;
}

export interface ContractTotals {
  months: number;
  billedMinor: number;
  recurringOursMinor: number;
  oneTimeOursMinor: number;
  oursMinor: number;
  ourCostMinor: number;
  contributionMinor: number;
  /** Over everything that reached us, one-time fees included. */
  marginPct: number | null;
}

export function contractTotals(months: readonly TimelineMonth[]): ContractTotals {
  const sum = (pick: (month: TimelineMonth) => number): number =>
    months.reduce((total, month) => total + pick(month), 0);
  const recurringOurs = sum((m) => m.recurringOursMinor);
  const oneTimeOurs = sum((m) => m.oneTimeOursMinor);
  const ours = recurringOurs + oneTimeOurs;
  const ourCost = sum((m) => m.ourCostMinor);
  return {
    months: months.length,
    billedMinor: sum((m) => m.customerBillMinor),
    recurringOursMinor: recurringOurs,
    oneTimeOursMinor: oneTimeOurs,
    oursMinor: ours,
    ourCostMinor: ourCost,
    contributionMinor: ours - ourCost,
    marginPct: ours <= 0 ? null : ((ours - ourCost) * 100) / ours,
  };
}

/** Per call and per minute, at full volume. Null where there is no volume. */
export interface UnitFigures {
  billedPerCallMinor: number | null;
  oursPerCallMinor: number | null;
  costPerCallMinor: number | null;
  billedPerMinuteMinor: number | null;
  contributionPerCallMinor: number | null;
}

export function unitFigures(figures: MonthFigures): UnitFigures {
  const per = (value: number, by: number): number | null => (by === 0 ? null : value / by);
  return {
    billedPerCallMinor: per(figures.recurringGrossMinor, figures.calls),
    oursPerCallMinor: per(figures.recurringOursMinor, figures.calls),
    costPerCallMinor: per(figures.costMinor, figures.calls),
    billedPerMinuteMinor: per(figures.recurringGrossMinor, figures.minutes),
    contributionPerCallMinor: per(figures.contributionMinor, figures.calls),
  };
}

/**
 * The rate, under `usage`, at which the full-volume month makes `targetPct`
 * margin, everything else in the plan held. Null when no rate can: nothing is
 * billed past the prepaid block, so the rate moves nothing, or the target is 100%
 * or more. Zero when the plan clears the target with the usage free.
 */
export function solveRate(
  scenario: Scenario,
  usage: UsagePricing,
  targetPct: number,
): number | null {
  if (targetPct >= 100) return null;
  const plan: Plan = { ...scenario.plan, usage };
  const at = monthAt(
    { ...scenario, plan: { ...plan, rateMinor: 0 } },
    scenario.volume.callsPerMonth,
  );
  if (scenario.shares.recurringPct <= 0) return null;
  // Margin t means ours = cost / (1 - t); gross = ours / share.
  const neededGross =
    at.ourCostMinor / (1 - targetPct / 100) / (scenario.shares.recurringPct / 100);
  if (at.recurringGrossMinor >= neededGross) return 0;
  if (at.overageUnits === 0) return null;
  return (neededGross - at.recurringGrossMinor) / at.overageUnits;
}

/** One way of pricing the same deal, for the side-by-side table. */
export interface PricingOption {
  key: string;
  kind: "entered" | "equivalent" | "sameRate" | "target";
  usage: UsagePricing;
  rateMinor: number;
  figures: MonthFigures;
  unit: UnitFigures;
}

/**
 * The plan as entered, then the same deal priced each other way: at the rate
 * that keeps the entered margin, at the entered per-minute rate with the other
 * rounding, and, given a target, at the rate that makes it.
 *
 * No rounded-up per-minute rate is solved for the entered margin: a rate that
 * only means something beside the per-second one it rounds is shown as that
 * rate rounded up (`sameRate`) instead.
 */
export function pricingOptions(scenario: Scenario, targetPct: number | null): PricingOption[] {
  const option = (
    key: string,
    kind: PricingOption["kind"],
    usage: UsagePricing,
    rateMinor: number,
  ): PricingOption => {
    const figures = steadyState({ ...scenario, plan: { ...scenario.plan, usage, rateMinor } });
    return { key, kind, usage, rateMinor, figures, unit: unitFigures(figures) };
  };
  const entered = option("entered", "entered", scenario.plan.usage, scenario.plan.rateMinor);
  const options = [entered];
  const margin = entered.figures.marginPct;
  if (margin !== null) {
    for (const usage of USAGE_PRICINGS) {
      if (usage === scenario.plan.usage || usage === "per_minute_whole") continue;
      const rate = solveRate(scenario, usage, margin);
      if (rate !== null) options.push(option(`equivalent-${usage}`, "equivalent", usage, rate));
    }
  }
  const otherRounding: UsagePricing | null =
    scenario.plan.usage === "per_minute"
      ? "per_minute_whole"
      : scenario.plan.usage === "per_minute_whole"
        ? "per_minute"
        : null;
  if (otherRounding !== null) {
    options.push(
      option(`same-${otherRounding}`, "sameRate", otherRounding, scenario.plan.rateMinor),
    );
  }
  if (targetPct !== null) {
    for (const usage of USAGE_PRICINGS) {
      const rate = solveRate(scenario, usage, targetPct);
      if (rate !== null) options.push(option(`target-${usage}`, "target", usage, rate));
    }
  }
  return options;
}

export interface CurvePoint {
  calls: number;
  marginPct: number | null;
  contributionMinor: number;
}

/**
 * Margin at full volume across a range of monthly calls: from none to well past
 * the scenario's volume and past where the prepaid block runs out, so both the flat
 * stretch inside the block and the slope after it are on the curve.
 */
export function volumeCurve(scenario: Scenario, steps = 40): CurvePoint[] {
  const top = curveTop(scenario);
  const points: CurvePoint[] = [];
  for (let step = 0; step <= steps; step += 1) {
    const calls = (top * step) / steps;
    const month = monthAt(scenario, calls);
    points.push({ calls, marginPct: month.marginPct, contributionMinor: month.contributionMinor });
  }
  return points;
}

/** The calls a month at which the prepaid block runs out. Null with no block. */
export function prepaidCalls(scenario: Scenario): number | null {
  const { plan, cost } = scenario;
  if (plan.prepaid.quantity <= 0) return null;
  if (plan.prepaid.unit === "calls") return plan.prepaid.quantity;
  const perCall = billedMinutesPerCall(plan.usage, cost);
  return perCall === 0 ? null : plan.prepaid.quantity / perCall;
}

function curveTop(scenario: Scenario): number {
  const calls = scenario.volume.callsPerMonth;
  const prepaid = prepaidCalls(scenario) ?? 0;
  return Math.max(calls * 2, prepaid * 1.5, 100);
}

/** What the customer saves against people, at full volume and over the first year. */
export interface Savings {
  humanMonthlyMinor: number;
  aiMonthlyMinor: number;
  monthlySavingsMinor: number;
  /** The first twelve months, implementation included, however long the term. */
  firstYearSavingsMinor: number;
  firstYearAiMinor: number;
  /** First-year savings over first-year spend on us, in percent. */
  firstYearRoiPct: number | null;
  /** Months of full-volume savings to recover the implementation fees. */
  paybackMonths: number | null;
  /** The share of the human cost a call through us saves, in percent. */
  savingsPct: number | null;
}

export function savings(scenario: Scenario): Savings {
  const steady = steadyState(scenario);
  const human = steady.calls * scenario.humanCostPerCallMinor;
  const ai = steady.recurringGrossMinor;
  const firstYear = timeline({ ...scenario, termMonths: 12 });
  const firstYearAi = firstYear.reduce((sum, month) => sum + month.customerBillMinor, 0);
  const firstYearSavings = firstYear.reduce((sum, month) => sum + month.savingsMinor, 0);
  const implementation = scenario.plan.implementation.reduce(
    (sum, fee) => sum + fee.amountMinor,
    0,
  );
  const monthly = human - ai;
  return {
    humanMonthlyMinor: human,
    aiMonthlyMinor: ai,
    monthlySavingsMinor: monthly,
    firstYearSavingsMinor: firstYearSavings,
    firstYearAiMinor: firstYearAi,
    firstYearRoiPct: firstYearAi <= 0 ? null : (firstYearSavings * 100) / firstYearAi,
    paybackMonths: monthly <= 0 ? null : implementation / monthly,
    savingsPct: human <= 0 ? null : (monthly * 100) / human,
  };
}

/** The book's margin for the month, and with this deal added to it at full volume. */
export interface BookImpact {
  beforeOursMinor: number;
  beforeContributionMinor: number;
  beforeMarginPct: number | null;
  afterOursMinor: number;
  afterContributionMinor: number;
  afterMarginPct: number | null;
}

export function bookImpact(totals: MarginTotals, deal: MonthFigures): BookImpact {
  // The report's margin is over the customers whose cost is known, so the
  // denominator is theirs: its contribution over its margin gives it back.
  const costedOurs =
    totals.margin_pct === null || totals.margin_pct === 0
      ? totals.recurring_our_share_minor
      : (totals.contribution_minor * 100) / totals.margin_pct;
  const afterOurs = costedOurs + deal.recurringOursMinor;
  const afterContribution = totals.contribution_minor + deal.contributionMinor;
  return {
    beforeOursMinor: costedOurs,
    beforeContributionMinor: totals.contribution_minor,
    beforeMarginPct: totals.margin_pct,
    afterOursMinor: afterOurs,
    afterContributionMinor: afterContribution,
    afterMarginPct: afterOurs <= 0 ? null : (afterContribution * 100) / afterOurs,
  };
}

/** How several customers are combined into one cost basis. */
export type Averaging = "weighted" | "mean";

/** A customer the cost basis can be measured from: one with a cost and with calls. */
export function isComparable(customer: MarginCustomer): boolean {
  return (
    customer.cost_minor !== null &&
    customer.volume !== null &&
    customer.volume.minutes > 0 &&
    customer.volume.conversations > 0
  );
}

/** Half a minute: what rounding up adds when calls end anywhere in their last minute. */
export const DEFAULT_ROUND_UP_MINUTES = 0.5;

/**
 * The cost basis of the chosen customers' month. Weighted pools them, so a
 * busy customer counts for more; mean averages each customer's own figures, so
 * every customer counts the same. Null with nothing comparable chosen.
 */
export function costBasisFrom(
  customers: readonly MarginCustomer[],
  averaging: Averaging,
): CostBasis | null {
  const measured = customers.filter(isComparable).map((customer) => {
    // isComparable has checked both.
    const volume = customer.volume!;
    return {
      cost: customer.cost_minor!,
      minutes: volume.minutes,
      calls: volume.conversations,
      wholeMinutes: volume.whole_minutes,
    };
  });
  if (measured.length === 0) return null;
  if (averaging === "weighted") {
    const cost = measured.reduce((sum, m) => sum + m.cost, 0);
    const minutes = measured.reduce((sum, m) => sum + m.minutes, 0);
    const calls = measured.reduce((sum, m) => sum + m.calls, 0);
    const whole = measured.reduce((sum, m) => sum + m.wholeMinutes, 0);
    return {
      costPerMinuteMinor: cost / minutes,
      minutesPerCall: minutes / calls,
      roundUpMinutesPerCall: Math.max(0, (whole - minutes) / calls),
    };
  }
  const mean = (pick: (m: (typeof measured)[number]) => number): number =>
    measured.reduce((sum, m) => sum + pick(m), 0) / measured.length;
  return {
    costPerMinuteMinor: mean((m) => m.cost / m.minutes),
    minutesPerCall: mean((m) => m.minutes / m.calls),
    roundUpMinutesPerCall: Math.max(
      0,
      mean((m) => (m.wholeMinutes - m.minutes) / m.calls),
    ),
  };
}

/** The chosen customers' cost, split by category (metered) or clause (recharged), summed. */
export function costSplit(
  customers: readonly MarginCustomer[],
): Array<{ key: string; label: string; costMinor: number }> {
  const byKey = new Map<string, { key: string; label: string; costMinor: number }>();
  for (const customer of customers.filter(isComparable)) {
    for (const line of customer.cost_lines) {
      const current = byKey.get(line.key);
      byKey.set(line.key, {
        key: line.key,
        label: current?.label ?? line.label,
        costMinor: (current?.costMinor ?? 0) + line.cost_minor,
      });
    }
  }
  return [...byKey.values()].sort((a, b) => b.costMinor - a.costMinor);
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
